import React, { useState, useEffect } from 'react';

const MembershipAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  const authHeadersForForm = {
    'Authorization': `Bearer ${adminToken}`
  };

  // ── State Variables ──
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [membershipUsers, setMembershipUsers] = useState([]);
  const [membershipStats, setMembershipStats] = useState({});
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [showDeletionRequests, setShowDeletionRequests] = useState(false);
  const [deletionConfirmModal, setDeletionConfirmModal] = useState({ show: false, request: null, action: null });
  const [selectedDeletionUserId, setSelectedDeletionUserId] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({});
  const [historyPage, setHistoryPage] = useState(1);
  const [historyDateFilter, setHistoryDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', '3months', 'custom'
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  // Login History modal sub-tab: 'history' (default 5 tabs) | 'devices' (new tab)
  const [historyTab, setHistoryTab] = useState('history');
  const [userDevices, setUserDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesLimit, setDevicesLimit] = useState(3);
  // Confirmation modal for device kick (instead of browser confirm())
  const [kickConfirm, setKickConfirm] = useState(null); // null | { device } | { all: true, count }
  const [kickBusy, setKickBusy] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [managingUser, setManagingUser] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [showGrantMembershipModal, setShowGrantMembershipModal] = useState(false);
  const [grantingUser, setGrantingUser] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [grantDurationMonths, setGrantDurationMonths] = useState(1);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
  email: '',
  password: '',
  full_name: '',
  username: '',
  planId: ''
  });
  const [showEmailUserModal, setShowEmailUserModal] = useState(false);
  const [emailUserData, setEmailUserData] = useState({
  userId: '',
  userEmail: '',
  userName: '',
  subject: '',
  message: ''
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    confirmText: '',
    confirmColor: '',
    onConfirm: null
  });
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState({
  name: '',
  display_name: '',
  price: 0,
  billing_period: 'monthly',
  description: '',
  is_active: true,
  is_popular: false,
  custom_features: [],
  display_features: [],
  features: {
  audio_enabled: false,
  favorites_enabled: false,
  daily_words_enabled: false,
  progress_tracking_enabled: false,
  voice_input_enabled: false,
  categories_enabled: false,
  games_access: false,
  travel_tools_access: false,
  conversation_challenge_access: false,
  request_word_enabled: false,
  translations_per_day: 5
  }
  });
  const [planToDelete, setPlanToDelete] = useState(null);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);

  // ── Data Loading ──
  const loadTabData = async () => {
    try {
      const [plansRes, usersRes, statsRes, deletionRes] = await Promise.all([
        fetch(`${API}/admin/membership/plans`, { headers: authHeaders }),
        fetch(`${API}/admin/membership/users`, { headers: authHeaders }),
        fetch(`${API}/admin/membership/stats`, { headers: authHeaders }),
        fetch(`${API}/admin/deletion-requests`, { headers: authHeaders })
      ]);

      if (plansRes.ok) {
        const d = await plansRes.json();
        setMembershipPlans(Array.isArray(d) ? d : []);
      }
      if (usersRes.ok) {
        const d = await usersRes.json();
        setMembershipUsers(Array.isArray(d) ? d : []);
      }
      if (statsRes.ok) {
        const d = await statsRes.json();
        setMembershipStats(d || {});
      }
      if (deletionRes.ok) {
        const d = await deletionRes.json();
        setDeletionRequests(Array.isArray(d) ? d : []);
      }
    } catch (error) {
      console.error('[MEMBERSHIP] Error loading data:', error);
    }
  };

  useEffect(() => {
    loadTabData();
  }, []);

  const loadMembershipUsers = async () => {
  console.log('[MEMBERSHIP] loadMembershipUsers called');
  try {
    const usersRes = await fetch(`${API}/admin/membership/users`, {
      headers: authHeaders
    });
    console.log('[MEMBERSHIP] loadMembershipUsers response:', usersRes.status);
    if (usersRes.ok) {
      const usersData = await usersRes.json();
      console.log('[MEMBERSHIP] loadMembershipUsers loaded:', usersData?.length || 0, 'users');
      setMembershipUsers(Array.isArray(usersData) ? usersData : []);
    } else {
      console.error('[MEMBERSHIP] Failed to load membership users:', usersRes.status);
      // Don't clear existing users on error
    }
  } catch (error) {
    console.error('[MEMBERSHIP] Error loading membership users:', error);
  }
  };
  const handleGrantMembership = async () => {
  if (!grantingUser || !selectedPlanId) {
    showNotification('Please select a plan', 'error');
    return;
  }
  
  try {
    const formData = new FormData();
    formData.append('plan_id', selectedPlanId);
    formData.append('duration_months', grantDurationMonths);
    
    const response = await fetch(`${API}/admin/users/${grantingUser.id}/grant-membership`, {
      method: 'POST',
      headers: authHeadersForForm,
      body: formData
    });
    
    if (response.ok) {
      const data = await response.json();
      showNotification(data.message || 'Membership granted successfully!', 'success');
      setShowGrantMembershipModal(false);
      setGrantingUser(null);
      setSelectedPlanId('');
      setGrantDurationMonths(1);
      // Reload users
      loadMembershipUsers();
    } else {
      const error = await response.json();
      showNotification(error.detail || 'Failed to grant membership', 'error');
    }
  } catch (error) {
    console.error('Error granting membership:', error);
    showNotification('Error granting membership', 'error');
  }
  };

  const createNewUser = async () => {
  if (!newUserData.email || !newUserData.password || !newUserData.full_name) {
    showNotification('Please fill all required fields', 'error');
    return;
  }

  try {
    const formData = new FormData();
    formData.append('email', newUserData.email);
    formData.append('password', newUserData.password);
    formData.append('full_name', newUserData.full_name);
    if (newUserData.username) {
      formData.append('username', newUserData.username);
    }

    console.log('[ADMIN] Creating user:', newUserData.email);
    
    const response = await fetch(`${API}/admin/users/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${adminToken}`
      },
      body: formData
    });

    console.log('[ADMIN] Create user response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('[ADMIN] User created successfully:', data);
      
      // If a plan was selected, grant it
      if (newUserData.planId && newUserData.planId !== '' && newUserData.planId !== 'free') {
        console.log('[ADMIN] Granting plan:', newUserData.planId);
        const grantFormData = new FormData();
        grantFormData.append('plan_id', newUserData.planId);
        grantFormData.append('duration_months', '12');
        
        try {
          const grantResponse = await fetch(`${API}/admin/users/${data.user.id}/grant-membership`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminToken}`
            },
            body: grantFormData
          });

          if (!grantResponse.ok) {
            const grantError = await grantResponse.json();
            console.warn('[ADMIN] Grant membership failed:', grantError);
            showNotification(`User created but membership grant failed: ${grantError.detail || 'Unknown error'}`, 'warning');
          } else {
            console.log('[ADMIN] Membership granted successfully');
          }
        } catch (grantErr) {
          console.error('[ADMIN] Grant membership error:', grantErr);
          showNotification('User created but membership grant failed', 'warning');
        }
      }

      showNotification('User created successfully!', 'success');
      setShowAddUserModal(false);
      setNewUserData({ email: '', password: '', full_name: '', username: '', planId: '' });
      
      // Force reload users list with delay to ensure DB is updated
      setTimeout(() => {
        console.log('[ADMIN] Reloading membership users...');
        loadMembershipUsers();
      }, 500);
    } else {
      const error = await response.json();
      console.error('[ADMIN] Create user failed:', error);
      showNotification(error.detail || 'Failed to create user', 'error');
    }
  } catch (error) {
    console.error('[ADMIN] Create user exception:', error);
    showNotification('Error creating user: ' + (error.message || 'Unknown error'), 'error');
  }
  };
  const handleEditUser = (user) => {
  setEditingUser({
    id: user.id,
    full_name: user.full_name,
    email: user.email
  });
  };
  const handleSaveUser = async () => {
  if (!editingUser) {
    showNotification('No user data to save', 'error');
    return;
  }

  try {
    const formData = new FormData();
    // Only send fields that have values
    if (editingUser.full_name) {
      formData.append('full_name', editingUser.full_name);
    }
    if (editingUser.email) {
      formData.append('email', editingUser.email);
    }

    // Only add password if it's provided (not empty)
    if (editingUser.password && editingUser.password.trim()) {
      formData.append('new_password', editingUser.password);
    }

    const response = await fetch(`${API}/admin/membership/users/${editingUser.id}`, {
      method: 'PUT',
      headers: authHeadersForForm,
      body: formData
    });

    if (response.ok) {
      showNotification('User updated successfully', 'success');
      setEditingUser(null);
      loadMembershipUsers();  // Refresh users list
      handleCloseUserManagement();  // Close the modal
    } else {
      const error = await response.json();
      console.error('Failed to update user:', error);
      showNotification(error.detail || 'Failed to update user', 'error');
    }
  } catch (error) {
    console.error('Failed to update user:', error);
    showNotification('Failed to update user', 'error');
  }
  };
  const handleUpdateUserStatus = async (userId, status) => {
  // Only require reason for ban/suspend if user wants to provide one
  // For activate, reason is never required
  
  try {
    const formData = new FormData();
    formData.append('status', status);
    
    // Only add reason if provided (optional for all actions)
    if (statusReason.trim()) {
      formData.append('reason', statusReason);
    }
    
    if (status === 'suspended' && suspendDays) {
      formData.append('suspend_days', suspendDays.toString());
    }
    
    const response = await fetch(`${API}/admin/membership/users/${userId}/status`, {
      method: 'PUT',
      headers: authHeadersForForm,
      body: formData
    });
    
    if (response.ok) {
      showNotification(`User ${status === 'active' ? 'activated' : status} successfully`, 'success');
      setShowStatusModal(false);
      setManagingUser(null);
      setStatusReason('');
      setSuspendDays(7);
      setStatusAction('');
      loadTabData();
    } else {
      const error = await response.json();
      showNotification(error.detail || 'Failed to update status', 'error');
    }
  } catch (error) {
    console.error('Error updating status:', error);
    showNotification('Failed to update status', 'error');
  }
  };
  const handleOpenStatusModal = (user) => {
  setManagingUser(user);
  setStatusAction('');
  setStatusReason('');
  setSuspendDays(7);
  setShowStatusModal(true);
  };
  const handleOpenUserManagement = (user) => {
  // Initialize editingUser with current user data so Save button works
  setEditingUser({
    id: user.id,
    full_name: user.full_name || '',
    email: user.email || '',
    password: ''
  });
  // Initialize editingSubscription with current subscription data
  // Format dates as YYYY-MM-DD so <input type="date"> can render them
  const fmtDate = (v) => {
    if (!v) return '';
    try {
      const d = new Date(v);
      return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
    } catch { return ''; }
  };
  setEditingSubscription(user.subscription ? {
    plan_id: user.subscription.plan_id || '',
    start_date: fmtDate(user.subscription.start_date),
    end_date: fmtDate(user.subscription.end_date),
    status: user.subscription.status || 'active'
  } : null);
  setManagingUser(user);
  setShowUserManagementModal(true);
  // Clear search to prevent browser autofill from filtering users
  setUserSearchQuery('');
  };
  const handleCloseUserManagement = (keepUser = false) => {
  setShowUserManagementModal(false);
  setEditingUser(null);  // Always reset editing state
  setUserSearchQuery('');  // Reset search to show all users
  if (!keepUser) {
    setManagingUser(null);
  }
  };
  const handleDeleteUser = async (userId, userName) => {
  try {
    const response = await fetch(`${API}/admin/membership/users/${userId}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    
    if (response.ok) {
      showNotification('User deleted successfully', 'success');
      loadTabData();
    } else {
      const error = await response.json();
      showNotification(error.detail || 'Failed to delete user', 'error');
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    showNotification('Failed to delete user', 'error');
  }
  };
  const handleViewHistory = async (userId, page = 1, dateFilter = 'all', startDate = '', endDate = '') => {
  try {
    // Build query parameters
    let url = `${API}/admin/membership/users/${userId}/history?page=${page}&limit=20`;
    
    // Apply date filters
    const now = new Date();
    let calculatedStartDate = startDate;
    let calculatedEndDate = endDate;
    
    if (dateFilter === 'today') {
      calculatedStartDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
      calculatedEndDate = new Date(now.setHours(23, 59, 59, 999)).toISOString();
    } else if (dateFilter === 'week') {
      calculatedStartDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (dateFilter === 'month') {
      calculatedStartDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    } else if (dateFilter === '3months') {
      calculatedStartDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    }
    
    if (calculatedStartDate) {
      url += `&start_date=${calculatedStartDate}`;
    }
    if (calculatedEndDate) {
      url += `&end_date=${calculatedEndDate}`;
    }
    
    const response = await fetch(url, {
      headers: authHeaders
    });
    
    if (response.ok) {
      const data = await response.json();
      setUserHistory(data.history || []);
      setHistoryPagination(data.pagination || {});
      setHistoryPage(page);
      setHistoryDateFilter(dateFilter);
      setHistoryStartDate(startDate);
      setHistoryEndDate(endDate);
      setShowHistoryModal(true);
    } else {
      showNotification('Failed to load history', 'error');
    }
  } catch (error) {
    console.error('Error loading history:', error);
    showNotification('Failed to load history', 'error');
  }
  };

  // ─── Admin device-management handlers ─────────────────────────────────────
  const handleViewDevices = async (userId) => {
    if (!userId) return;
    setDevicesLoading(true);
    setHistoryTab('devices');
    try {
      const r = await fetch(`${API}/admin/users/${userId}/sessions`, { headers: authHeaders });
      if (r.ok) {
        const data = await r.json();
        setUserDevices(data.devices || []);
        setDevicesLimit(data.device_limit || 3);
      } else {
        showNotification('Failed to load devices', 'error');
      }
    } catch (e) {
      console.error('Error loading devices:', e);
      showNotification('Failed to load devices', 'error');
    } finally {
      setDevicesLoading(false);
    }
  };

  // These now just OPEN the confirmation modal. The actual fetch happens in confirmKick below.
  const handleKickDevice = (userId, device) => {
    if (!userId || !device) return;
    setKickConfirm({ userId, device });
  };

  const handleKickAllDevices = (userId) => {
    if (!userId) return;
    setKickConfirm({ userId, all: true, count: userDevices.length });
  };

  const confirmKick = async () => {
    if (!kickConfirm || kickBusy) return;
    setKickBusy(true);
    try {
      const isAll = !!kickConfirm.all;
      const url = isAll
        ? `${API}/admin/users/${kickConfirm.userId}/sessions`
        : `${API}/admin/users/${kickConfirm.userId}/sessions/${kickConfirm.device.device_id}`;
      const r = await fetch(url, { method: 'DELETE', headers: authHeaders });
      if (r.ok) {
        if (isAll) {
          const data = await r.json();
          showNotification(`Signed out ${data.removed || 0} device(s)`, 'success');
        } else {
          showNotification('Device signed out', 'success');
        }
        setKickConfirm(null);
        await handleViewDevices(kickConfirm.userId);
      } else {
        showNotification('Failed to sign out', 'error');
      }
    } catch (e) {
      showNotification('Failed to sign out', 'error');
    } finally {
      setKickBusy(false);
    }
  };

  const handleExportUsers = (format) => {
  const filteredUsers = membershipUsers.filter(user =>
    (user.full_name || '').toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(userSearchQuery.toLowerCase())
  );
  
  if (format === 'csv') {
    const csv = [
      ['Name', 'Email', 'Plan', 'Status', 'Signup Date', 'Last Login', 'IP Address'].join(','),
      ...filteredUsers.map(user => [
        user.full_name,
        user.email,
        user.plan?.display_name || 'No Plan',
        user.status || 'active',
        new Date(user.created_at).toLocaleString(),
        user.last_login ? new Date(user.last_login).toLocaleString() : 'Never',
        user.last_ip || 'N/A'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  } else if (format === 'txt') {
    const txt = filteredUsers.map(user =>
      `Name: ${user.full_name}\nEmail: ${user.email}\nPlan: ${user.plan?.display_name || 'No Plan'}\nStatus: ${user.status || 'active'}\nSignup Date: ${new Date(user.created_at).toLocaleString()}\nLast Login: ${user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}\nIP Address: ${user.last_ip || 'N/A'}\n${'='.repeat(50)}\n`
    ).join('\n');
    
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  }
  
  showNotification(`Exported ${filteredUsers.length} users as ${format.toUpperCase()}`, 'success');
  };

  return (
    <>
  <div className="space-y-6">
    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
      👥 Membership Management
    </h3>
    
    {/* Stats Cards */}
    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>Total Users</p>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {(membershipStats || {}).total_users || (membershipUsers || []).length}
        </p>
      </div>
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-emerald-900/20 border border-emerald-700' : 'bg-emerald-50 border border-emerald-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Active Users (24h)</p>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {(membershipStats || {}).active_users_24h || 0}
        </p>
      </div>
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>Free Users</p>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {(membershipUsers || []).filter(u => u.plan?.name === 'free').length}
        </p>
      </div>
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50 border border-purple-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>Paid Users</p>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {(membershipUsers || []).filter(u => u.plan?.name !== 'free').length}
        </p>
      </div>
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-orange-900/20 border border-orange-700' : 'bg-orange-50 border border-orange-200'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>Banned/Suspended</p>
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {((membershipStats || {}).banned || 0) + ((membershipStats || {}).suspended || 0)}
        </p>
      </div>
      {/* Deletion Requests Card - Clickable */}
      <button
        onClick={() => setShowDeletionRequests(!showDeletionRequests)}
        className={`p-4 rounded-lg text-left transition-all ${
          (deletionRequests || []).length > 0
            ? isDarkMode ? 'bg-red-900/30 border-2 border-red-500 hover:bg-red-900/50' : 'bg-red-50 border-2 border-red-400 hover:bg-red-100'
            : isDarkMode ? 'bg-gray-800 border border-gray-700 hover:bg-gray-700' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
        }`}
      >
        <p className={`text-sm ${(deletionRequests || []).length > 0 ? (isDarkMode ? 'text-red-300' : 'text-red-600') : (isDarkMode ? 'text-gray-400' : 'text-gray-600')}`}>
          🗑️ Deletion Requests
        </p>
        <p className={`text-2xl font-bold ${(deletionRequests || []).length > 0 ? 'text-red-500' : (isDarkMode ? 'text-white' : 'text-gray-800')}`}>
          {(deletionRequests || []).length}
          {(deletionRequests || []).length > 0 && <span className="ml-2 text-sm animate-pulse">⚠️</span>}
        </p>
      </button>
    </div>

    {/* Deletion Requests Section - Shows when clicked */}
    {showDeletionRequests && (
      <div className={`p-6 rounded-lg border-2 ${isDarkMode ? 'bg-red-900/10 border-red-700' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
            🗑️ Account Deletion Requests ({deletionRequests.length})
          </h4>
          <button
            onClick={() => setShowDeletionRequests(false)}
            className={`px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
          >
            ✕ Close
          </button>
        </div>
        
        {deletionRequests.length === 0 ? (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="text-4xl mb-2">✅</p>
            <p>No pending deletion requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deletionRequests.map(req => (
              <div key={req.id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-red-500">⚠️</span>
                      <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{req.user_name || 'Unknown'}</span>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({req.user_email})</span>
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <p><strong>Reason:</strong> {req.reason || 'Not specified'}</p>
                      {req.details && <p><strong>Details:</strong> {req.details}</p>}
                      <p><strong>Requested:</strong> {new Date(req.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => setDeletionConfirmModal({ show: true, request: req, action: 'keep' })}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      title="Keep user account"
                    >
                      ✓ Keep Account
                    </button>
                    <button
                      onClick={() => setDeletionConfirmModal({ show: true, request: req, action: 'delete' })}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      title="Permanently delete account"
                    >
                      🗑️ Delete Account
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Membership Plans Section */}
    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Membership Plans
        </h4>
        <button
          onClick={() => {
            setNewPlan({
              name: '',
              display_name: '',
              price: 0,
              billing_period: 'monthly',
              description: '',
              is_active: true,
              features: {
                audio_enabled: false,
                favorites_enabled: false,
                daily_words_enabled: false,
                progress_tracking_enabled: false,
                voice_input_enabled: false,
                categories_enabled: false,
                translations_per_day: 5
              }
            });
            setEditingPlan('new');
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
        >
          ➕ Add New Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {membershipPlans.map(plan => {
          console.log(`[PLAN RENDER] ${plan.display_name}: is_active=${plan.is_active}, type=${typeof plan.is_active}`);
          return (
          <div
            key={plan.id}
            className={`p-4 rounded-lg border ${
              plan.is_active 
                ? isDarkMode ? 'border-green-700 bg-green-900/10' : 'border-green-200 bg-green-50'
                : isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h5 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {plan.display_name}
                </h5>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {plan.name}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                plan.is_active 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-500 text-white'
              }`}>
                {plan.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div className="mb-3">
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                ${plan.price}
                <span className={`text-sm font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  /{plan.billing_period}
                </span>
              </p>
            </div>
            
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {plan.description}
            </p>
            
            <div className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <p>Translations: {plan.features.translations_per_day === -1 ? 'Unlimited' : `${plan.features.translations_per_day}/day`}</p>
              <p>Audio: {plan.features.audio_enabled ? '✅' : '❌'}</p>
              <p>Favorites: {plan.features.favorites_enabled ? '✅' : '❌'}</p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // Auto-generate display_features if plan doesn't have them yet
                  if (!plan.display_features || plan.display_features.length === 0) {
                    const BUILTIN_FEATURES = [
                      { key: 'audio_enabled', label: 'Audio Pronunciation', icon: '🔊' },
                      { key: 'favorites_enabled', label: 'Save Favorites', icon: '❤️' },
                      { key: 'daily_words_enabled', label: 'Explore (Listen & Guess)', icon: '🎧' },
                      { key: 'progress_tracking_enabled', label: 'Word Bank', icon: '\u{1F4DA}' },
                      { key: 'voice_input_enabled', label: 'Voice Input', icon: '🎤' },
                      { key: 'categories_enabled', label: 'Learning Categories', icon: '📂' },
                      { key: 'games_access', label: 'Games Access', icon: '🎮' },
                      { key: 'travel_tools_access', label: 'Travel Tools Dictionary', icon: '🇲🇦' },
                      { key: 'conversation_challenge_access', label: 'Conversation Challenge', icon: '💬' },
                      { key: 'request_word_enabled', label: 'Request New Words', icon: '➕' }
                    ];
                    const items = [];
                    if (plan.features?.translations_per_day === -1) {
                      items.push({ type: 'builtin', key: 'translations_per_day', icon: '🔄', label: 'Unlimited translations', enabled: true });
                    } else if (plan.features?.translations_per_day > 0) {
                      items.push({ type: 'builtin', key: 'translations_per_day', icon: '🔄', label: `${plan.features.translations_per_day} translations/day`, enabled: true });
                    }
                    BUILTIN_FEATURES.forEach(bf => {
                      items.push({ type: 'builtin', key: bf.key, icon: bf.icon, label: bf.label, enabled: !!plan.features?.[bf.key] });
                    });
                    (plan.custom_features || []).forEach(cf => {
                      if (cf) items.push({ type: 'custom', key: null, icon: '✨', label: cf, enabled: true });
                    });
                    setMembershipPlans(prev => prev.map(p => p.id === plan.id ? {...p, display_features: items} : p));
                  }
                  setEditingPlan(plan.id);
                }}
                className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => {
                  setPlanToDelete(plan);
                  setShowDeletePlanModal(true);
                }}
                className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                🗑️
              </button>
            </div>
          </div>
        );
        })}
      </div>
    </div>

    {/* Edit Plan Modal */}
    {editingPlan && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="sticky top-0 p-6 border-b z-10" style={{background: isDarkMode ? '#1f2937' : '#ffffff'}}>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {editingPlan === 'new' ? 'Create New Plan' : 'Edit Plan'}
            </h3>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Plan Name (internal)
              </label>
              <input
                type="text"
                value={editingPlan === 'new' ? newPlan.name : membershipPlans.find(p => p.id === editingPlan)?.name || ''}
                onChange={(e) => {
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, name: e.target.value});
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="free, monthly, yearly"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Display Name
              </label>
              <input
                type="text"
                value={editingPlan === 'new' ? newPlan.display_name : membershipPlans.find(p => p.id === editingPlan)?.display_name || ''}
                onChange={(e) => {
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, display_name: e.target.value});
                  } else {
                    setMembershipPlans(membershipPlans.map(p => 
                      p.id === editingPlan ? {...p, display_name: e.target.value} : p
                    ));
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="Monthly Pro"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingPlan === 'new' ? newPlan.price : membershipPlans.find(p => p.id === editingPlan)?.price || 0}
                  onChange={(e) => {
                    const price = parseFloat(e.target.value);
                    if (editingPlan === 'new') {
                      setNewPlan({...newPlan, price});
                    } else {
                      setMembershipPlans(membershipPlans.map(p => 
                        p.id === editingPlan ? {...p, price} : p
                      ));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Billing Period
                </label>
                <select
                  value={editingPlan === 'new' ? newPlan.billing_period : membershipPlans.find(p => p.id === editingPlan)?.billing_period || 'monthly'}
                  onChange={(e) => {
                    if (editingPlan === 'new') {
                      setNewPlan({...newPlan, billing_period: e.target.value});
                    } else {
                      setMembershipPlans(membershipPlans.map(p => 
                        p.id === editingPlan ? {...p, billing_period: e.target.value} : p
                      ));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="once">Once</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>

            {/* Stripe Price ID — required for paid plans (auto-renew subscriptions) */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Stripe Price ID <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(paid plans only — leave blank for Free)</span>
              </label>
              <input
                type="text"
                value={editingPlan === 'new' ? (newPlan.stripe_price_id || '') : membershipPlans.find(p => p.id === editingPlan)?.stripe_price_id || ''}
                onChange={(e) => {
                  const stripe_price_id = e.target.value.trim();
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, stripe_price_id});
                  } else {
                    setMembershipPlans(membershipPlans.map(p =>
                      p.id === editingPlan ? {...p, stripe_price_id} : p
                    ));
                  }
                }}
                placeholder="price_1Q3RbcA1Bcd2efg3HiJ4kLmN"
                className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Find in Stripe Dashboard → Products → click your product → click the recurring price → copy ID (starts with <code>price_</code>).
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description
              </label>
              <textarea
                value={editingPlan === 'new' ? newPlan.description : membershipPlans.find(p => p.id === editingPlan)?.description || ''}
                onChange={(e) => {
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, description: e.target.value});
                  } else {
                    setMembershipPlans(membershipPlans.map(p => 
                      p.id === editingPlan ? {...p, description: e.target.value} : p
                    ));
                  }
                }}
                rows="2"
                className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="Plan description"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Translations Per Day
              </label>
              <input
                type="number"
                value={editingPlan === 'new' ? newPlan.features.translations_per_day : membershipPlans.find(p => p.id === editingPlan)?.features?.translations_per_day || 5}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, features: {...newPlan.features, translations_per_day: value}});
                  } else {
                    setMembershipPlans(membershipPlans.map(p => 
                      p.id === editingPlan ? {...p, features: {...p.features, translations_per_day: value}} : p
                    ));
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Use -1 for unlimited
              </p>
            </div>
            
            <div className={`border-t pt-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Plan Status
              </p>
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={editingPlan === 'new' ? (newPlan.is_active !== undefined ? newPlan.is_active : true) : (membershipPlans.find(p => p.id === editingPlan)?.is_active !== undefined ? membershipPlans.find(p => p.id === editingPlan)?.is_active : true)}
                  onChange={(e) => {
                    if (editingPlan === 'new') {
                      setNewPlan({...newPlan, is_active: e.target.checked});
                    } else {
                      setMembershipPlans(membershipPlans.map(p =>
                        p.id === editingPlan ? {...p, is_active: e.target.checked} : p
                      ));
                    }
                  }}
                  className="rounded w-5 h-5"
                />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Active (Show in frontend for users to purchase)
                </span>
              </label>
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={editingPlan === 'new' ? (newPlan.is_popular || false) : (membershipPlans.find(p => p.id === editingPlan)?.is_popular || false)}
                  onChange={(e) => {
                    if (editingPlan === 'new') {
                      setNewPlan({...newPlan, is_popular: e.target.checked});
                    } else {
                      setMembershipPlans(membershipPlans.map(p =>
                        p.id === editingPlan ? {...p, is_popular: e.target.checked} : p
                      ));
                    }
                  }}
                  className="rounded w-5 h-5"
                />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ⭐ Popular (Show "POPULAR" badge on plan card)
                </span>
              </label>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Only active plans will be visible to users. Inactive plans are hidden but existing subscriptions remain active.
              </p>
            </div>
            
            <div className={`border-t pt-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Feature Access
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Toggle, reorder, edit or add features. Drag to reorder.
                </p>
              </div>
              {(() => {
                const BUILTIN_FEATURES = [
                  { key: 'audio_enabled', label: 'Audio Pronunciation', icon: '🔊' },
                  { key: 'favorites_enabled', label: 'Save Favorites', icon: '❤️' },
                  { key: 'daily_words_enabled', label: 'Explore (Listen & Guess)', icon: '🎧' },
                  { key: 'progress_tracking_enabled', label: 'Word Bank', icon: '\u{1F4DA}' },
                  { key: 'voice_input_enabled', label: 'Voice Input', icon: '🎤' },
                  { key: 'categories_enabled', label: 'Learning Categories', icon: '📂' },
                  { key: 'games_access', label: 'Games Access', icon: '🎮' },
                  { key: 'travel_tools_access', label: 'Travel Tools Dictionary', icon: '🇲🇦' },
                  { key: 'conversation_challenge_access', label: 'Conversation Challenge', icon: '💬' },
                  { key: 'request_word_enabled', label: 'Request New Words', icon: '➕' }
                ];
                const plan = editingPlan === 'new' ? newPlan : membershipPlans.find(p => p.id === editingPlan);
                if (!plan) return null;

                // Build unified display list from display_features or generate default
                const getDisplayFeatures = () => {
                  if (plan.display_features && plan.display_features.length > 0) return plan.display_features;
                  // Generate from existing data for backwards compat
                  const items = [];
                  // Translations
                  if (plan.features?.translations_per_day === -1) {
                    items.push({ type: 'builtin', key: 'translations_per_day', icon: '🔄', label: 'Unlimited translations', enabled: true });
                  } else if (plan.features?.translations_per_day > 0) {
                    items.push({ type: 'builtin', key: 'translations_per_day', icon: '🔄', label: `${plan.features.translations_per_day} translations/day`, enabled: true });
                  }
                  BUILTIN_FEATURES.forEach(bf => {
                    items.push({ type: 'builtin', key: bf.key, icon: bf.icon, label: bf.label, enabled: !!plan.features?.[bf.key] });
                  });
                  (plan.custom_features || []).forEach(cf => {
                    if (cf) items.push({ type: 'custom', key: null, icon: '✨', label: cf, enabled: true });
                  });
                  return items;
                };

                const displayFeatures = getDisplayFeatures();

                const updatePlan = (updates) => {
                  if (editingPlan === 'new') {
                    setNewPlan({...newPlan, ...updates});
                  } else {
                    setMembershipPlans(membershipPlans.map(p =>
                      p.id === editingPlan ? {...p, ...updates} : p
                    ));
                  }
                };

                const setDisplayFeatures = (newList) => {
                  // Sync back to features object and custom_features for backend compat
                  const featuresObj = {...(plan.features || {})};
                  const customFeats = [];
                  // Reset all builtin features to false first
                  BUILTIN_FEATURES.forEach(bf => { featuresObj[bf.key] = false; });
                  newList.forEach(item => {
                    if (item.type === 'builtin' && item.key && item.key !== 'translations_per_day') {
                      featuresObj[item.key] = item.enabled;
                    }
                    if (item.type === 'custom' && item.label) {
                      customFeats.push(item.label);
                    }
                  });
                  updatePlan({ display_features: newList, features: featuresObj, custom_features: customFeats });
                };

                return (
                  <>
                    <div className="space-y-1 mb-3">
                      {displayFeatures.map((item, idx) => (
                        <div
                          key={idx}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.style.opacity = '0.4'; }}
                          onDragEnd={(e) => { e.currentTarget.style.opacity = '1'; }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderTop = '2px solid #8b5cf6'; }}
                          onDragLeave={(e) => { e.currentTarget.style.borderTop = ''; }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.borderTop = '';
                            const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                            if (fromIdx === idx) return;
                            const updated = [...displayFeatures];
                            const [moved] = updated.splice(fromIdx, 1);
                            updated.splice(idx, 0, moved);
                            setDisplayFeatures(updated);
                          }}
                          className={`flex items-center gap-1.5 group rounded-lg px-2 py-1.5 transition-colors ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${!item.enabled ? 'opacity-50' : ''}`}
                          style={{ cursor: 'grab' }}
                        >
                          <span className={`text-sm cursor-grab select-none flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} title="Drag to reorder">⠿</span>
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={(e) => {
                              const updated = [...displayFeatures];
                              updated[idx] = {...updated[idx], enabled: e.target.checked};
                              setDisplayFeatures(updated);
                            }}
                            className="rounded flex-shrink-0 w-4 h-4"
                            title="Show on plan card"
                          />
                          <span className="flex-shrink-0 w-6 text-center">{item.icon}</span>
                          <input
                            type="text"
                            value={item.label}
                            onChange={(e) => {
                              const updated = [...displayFeatures];
                              updated[idx] = {...updated[idx], label: e.target.value};
                              setDisplayFeatures(updated);
                            }}
                            className={`flex-1 px-2 py-1 text-sm border rounded min-w-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Feature text..."
                          />
                          {item.type !== 'builtin' && (
                            <input
                              type="text"
                              value={item.icon}
                              onChange={(e) => {
                                const updated = [...displayFeatures];
                                updated[idx] = {...updated[idx], icon: e.target.value};
                                setDisplayFeatures(updated);
                              }}
                              className={`w-10 px-1 py-1 text-sm text-center border rounded flex-shrink-0 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                              title="Icon/emoji"
                            />
                          )}
                          <button
                            onClick={() => {
                              if (idx > 0) {
                                const updated = [...displayFeatures];
                                [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                                setDisplayFeatures(updated);
                              }
                            }}
                            disabled={idx === 0}
                            className={`text-xs px-0.5 flex-shrink-0 ${idx === 0 ? (isDarkMode ? 'text-gray-600' : 'text-gray-300') : 'text-gray-500 hover:text-blue-600'}`}
                            title="Move up"
                          >▲</button>
                          <button
                            onClick={() => {
                              if (idx < displayFeatures.length - 1) {
                                const updated = [...displayFeatures];
                                [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
                                setDisplayFeatures(updated);
                              }
                            }}
                            disabled={idx === displayFeatures.length - 1}
                            className={`text-xs px-0.5 flex-shrink-0 ${idx === displayFeatures.length - 1 ? (isDarkMode ? 'text-gray-600' : 'text-gray-300') : 'text-gray-500 hover:text-blue-600'}`}
                            title="Move down"
                          >▼</button>
                          <button
                            onClick={() => {
                              const updated = displayFeatures.filter((_, i) => i !== idx);
                              setDisplayFeatures(updated);
                            }}
                            className="text-red-400 hover:text-red-600 text-sm px-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove"
                          >✕</button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDisplayFeatures([...displayFeatures, { type: 'custom', key: null, icon: '✨', label: '', enabled: true }])}
                        className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-all ${isDarkMode ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                      >
                        + Add Feature
                      </button>
                      {displayFeatures.length > 0 && (
                        <button
                          onClick={() => setDisplayFeatures([])}
                          className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-all ${isDarkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-50 text-red-500 hover:bg-red-100'}`}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          
          <div className={`sticky bottom-0 p-6 border-t flex gap-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <button
              onClick={() => setEditingPlan(null)}
              className={`flex-1 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  const planData = editingPlan === 'new' ? newPlan : membershipPlans.find(p => p.id === editingPlan);
                  
                  console.log('[DEBUG] Saving plan:', planData);
                  console.log('[DEBUG] is_active value:', planData?.is_active);
                  
                  if (editingPlan === 'new') {
                    const response = await fetch(`${API}/admin/membership/plans`, {
                      method: 'POST',
                      headers: {
                        ...authHeaders,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(planData)
                    });
                    
                    if (response.ok) {
                      showNotification('Plan created successfully', 'success');
                      setEditingPlan(null);
                      loadTabData();
                    }
                  } else {
                    const response = await fetch(`${API}/admin/membership/plans/${editingPlan}`, {
                      method: 'PUT',
                      headers: {
                        ...authHeaders,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(planData)
                    });
                    
                    console.log('[DEBUG] Update response:', response.status);
                    
                    if (response.ok) {
                      showNotification('Plan updated successfully', 'success');
                      setEditingPlan(null);
                      loadTabData();
                    } else {
                      const error = await response.json();
                      console.error('[DEBUG] Update failed:', error);
                      showNotification('Failed to update plan', 'error');
                    }
                  }
                } catch (error) {
                  console.error('[DEBUG] Save error:', error);
                  showNotification('Failed to save plan', 'error');
                }
              }}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              {editingPlan === 'new' ? 'Create Plan' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Delete Plan Confirmation Modal */}
    {showDeletePlanModal && planToDelete && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-md w-full rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Delete Membership Plan
                </h3>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {planToDelete.display_name}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                ${planToDelete.price}/{planToDelete.billing_period}
              </p>
            </div>
            
            <div className={`p-4 rounded-lg mb-6 border-l-4 border-red-500 ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
              <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                • This will permanently delete the plan
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                • Existing user subscriptions will remain active
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                • Users won't be able to purchase this plan anymore
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeletePlanModal(false);
                  setPlanToDelete(null);
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${API}/admin/membership/plans/${planToDelete.id}`, {
                      method: 'DELETE',
                      headers: authHeaders
                    });
                    if (response.ok) {
                      showNotification('Plan deleted successfully', 'success');
                      setShowDeletePlanModal(false);
                      setPlanToDelete(null);
                      loadTabData();
                    } else {
                      showNotification('Failed to delete plan', 'error');
                    }
                  } catch (error) {
                    showNotification('Failed to delete plan', 'error');
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                🗑️ Delete Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Users Section */}
    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Registered Users ({(membershipUsers || []).filter(u =>
            (u.full_name || '').toLowerCase().includes((userSearchQuery || '').toLowerCase()) ||
            (u.email || '').toLowerCase().includes((userSearchQuery || '').toLowerCase())
          ).length})
        </h4>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddUserModal(true)}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
          >
            ➕ Add User
          </button>
          <button
            onClick={() => handleExportUsers('csv')}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            📥 Export CSV
          </button>
          <button
            onClick={() => handleExportUsers('txt')}
            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            📥 Export TXT
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="🔍 Search by name or email..."
          value={userSearchQuery || ''}
          onChange={(e) => setUserSearchQuery(e.target.value || '')}
          name="membership-user-search-xyz"
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-form-type="other"
          className={`w-full px-4 py-2 border rounded-lg ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>ID</th>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>User / Email</th>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Plan</th>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Member Since</th>
              <th className={`text-left p-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(membershipUsers || [])
              .filter(u => {
                const searchTerm = (userSearchQuery || '').toLowerCase();
                if (!searchTerm) return true;
                const nameMatch = (u.full_name || '').toLowerCase().includes(searchTerm);
                const emailMatch = (u.email || '').toLowerCase().includes(searchTerm);
                return nameMatch || emailMatch;
              })
              .map((user, index) => (
              <tr key={user.id || index} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className="p-3">
                  <p className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {index + 1}
                  </p>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{user.full_name || 'Unknown User'}</p>
                        {(() => {
                          const delReq = (deletionRequests || []).find(r => r.user_id === user.id);
                          if (!delReq) return null;
                          const requestDate = new Date(delReq.created_at);
                          const deleteByDate = new Date(requestDate.getTime() + 30 * 24 * 60 * 60 * 1000);
                          return (
                            <div className="relative inline-block">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDeletionUserId(selectedDeletionUserId === user.id ? null : user.id);
                                }}
                                className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded animate-pulse cursor-pointer hover:bg-red-600"
                              >
                                🗑️ DELETION
                              </button>
                              <div className="mt-0.5">
                                <p className={`text-[10px] leading-tight ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                  Requested: {requestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} {requestDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </p>
                                <p className={`text-[10px] leading-tight font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                  Delete by: {deleteByDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                              {selectedDeletionUserId === user.id && (
                                <div className={`absolute z-50 top-full left-0 mt-1 w-72 p-4 rounded-lg shadow-xl border-2 ${
                                  isDarkMode ? 'bg-gray-800 border-red-600' : 'bg-white border-red-300'
                                }`}>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedDeletionUserId(null); }}
                                    className={`absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                                      isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                    }`}
                                  >
                                    ✕
                                  </button>
                                  <p className={`font-semibold text-sm mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                    ⚠️ Why they want to leave:
                                  </p>
                                  <p className={`text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    <strong>Reason:</strong> {delReq.reason || 'Not specified'}
                                  </p>
                                  {delReq.details && (
                                    <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      <strong>Details:</strong> {delReq.details}
                                    </p>
                                  )}
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Requested: {new Date(delReq.created_at).toLocaleString()}
                                  </p>
                                  <p className={`text-xs mb-3 font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                    ⏳ Delete by: {new Date(new Date(delReq.created_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                  </p>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setDeletionConfirmModal({ show: true, request: delReq, action: 'keep' });
                                        setSelectedDeletionUserId(null);
                                      }}
                                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
                                    >
                                      ✓ Keep Account
                                    </button>
                                    <button
                                      onClick={() => {
                                        setDeletionConfirmModal({ show: true, request: delReq, action: 'delete' });
                                        setSelectedDeletionUserId(null);
                                      }}
                                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-semibold"
                                    >
                                      🗑️ Delete Account
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{user.email || 'No email'}</p>
                      <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        IP: {user.last_ip || 'N/A'}
                      </p>
                    </div>
                    {user.is_admin && (
                      <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded">
                        ADMIN
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  {/* Plan Badge with distinctive colors */}
                  {(() => {
                    const planName = user.plan?.display_name || 'Free Plan';
                    const isProPlan = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('monthly') || planName.toLowerCase().includes('yearly');
                    const isFreePlan = planName.toLowerCase().includes('free');
                    
                    {/* MEMBERSHIP FIX: Show start/end dates under plan badge for Pro users */}
                    const subStartDate = user.subscription?.start_date;
                    const subEndDate = user.subscription?.end_date || user.subscription?.expiration_date;
                    return (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                            isProPlan
                              ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white shadow-lg'
                              : isFreePlan
                              ? isDarkMode ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-blue-100 text-blue-700 border border-blue-300'
                              : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {isProPlan && <span>⭐</span>}
                            {isFreePlan && <span>🆓</span>}
                            {planName}
                          </span>
                        </div>
                        {isProPlan && subStartDate && (
                          <p className={`text-[10px] mt-1 leading-tight ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Start: {new Date(subStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        {isProPlan && subEndDate && (
                          <p className={`text-[10px] leading-tight font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            Expires: {new Date(subEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  {user.subscription?.will_cancel && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                      Cancelled on {(() => {
                        try {
                          if (user.subscription?.cancelled_at) {
                            const d = new Date(user.subscription.cancelled_at);
                            return !isNaN(d.getTime()) ? d.toLocaleDateString() : 'N/A';
                          }
                          return 'N/A';
                        } catch { return 'N/A'; }
                      })()}
                    </p>
                  )}
                </td>
                <td className="p-3">
                  <div>
                    <div className="flex flex-wrap gap-1 mb-1">
                      <span className={`text-xs px-2 py-1 rounded-full inline-block text-center ${
                        user.status === 'banned' 
                          ? 'bg-red-500 text-white'
                          : user.status === 'suspended'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-green-500 text-white'
                      }`}>
                        {user.status === 'banned' ? 'Banned' : user.status === 'suspended' ? 'Suspended' : 'Active'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full inline-block text-center ${
                        user.email_verified
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-500 text-white'
                      }`}>
                        {user.email_verified ? '✅ Verified' : '⏸️ Pending'}
                      </span>
                    </div>
                    {user.subscription?.will_cancel && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                        Subscription ends: {(() => {
                          try {
                            if (user.subscription?.end_date) {
                              const d = new Date(user.subscription.end_date);
                              return !isNaN(d.getTime()) ? d.toLocaleDateString() : 'N/A';
                            }
                            return 'N/A';
                          } catch { return 'N/A'; }
                        })()}
                      </p>
                    )}
                    {user.status === 'banned' && user.ban_reason && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                        <strong>Reason:</strong> {user.ban_reason}
                      </p>
                    )}
                    {user.status === 'suspended' && user.suspend_reason && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        <strong>Reason:</strong> {user.suspend_reason}
                      </p>
                    )}
                    {user.status === 'suspended' && user.suspended_until && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        <strong>Until:</strong> {(() => {
                          try {
                            const d = new Date(user.suspended_until);
                            return !isNaN(d.getTime()) ? d.toLocaleString() : 'N/A';
                          } catch { return 'N/A'; }
                        })()}
                      </p>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <div>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {(() => {
                        try {
                          const d = new Date(user.created_at);
                          return !isNaN(d.getTime()) ? d.toLocaleDateString() : 'N/A';
                        } catch { return 'N/A'; }
                      })()}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {(() => {
                        try {
                          const d = new Date(user.created_at);
                          return !isNaN(d.getTime()) ? d.toLocaleTimeString() : '';
                        } catch { return ''; }
                      })()}
                    </p>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex flex-col gap-2">
                    {/* Main Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleOpenUserManagement(user)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        title="Edit user"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => {
                          setEmailUserData({
                            userId: user.id,
                            userEmail: user.email,
                            userName: user.full_name,
                            subject: '',
                            message: ''
                          });
                          setShowEmailUserModal(true);
                        }}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        title="Send email to user"
                      >
                        ✉️ Email
                      </button>
                      <button
                        onClick={() => {
                          setConfirmModalData({
                            title: '🗑️ Delete User',
                            message: `Are you sure you want to delete "${user.full_name}" (${user.email})?\n\n⚠️ This action cannot be undone and will permanently delete:\n• User account\n• All subscription data\n• Usage history\n• Login history`,
                            confirmText: 'Delete User',
                            confirmColor: 'red',
                            onConfirm: () => {
                              handleDeleteUser(user.id, user.full_name);
                              setShowConfirmModal(false);
                            }
                          });
                          setShowConfirmModal(true);
                        }}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                        title="Delete user"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    
                    {/* Email Verification Controls */}
                    <div className="flex gap-1">
                      {!user.email_verified && (
                        <button
                          onClick={() => {
                            setConfirmModalData({
                              title: '✅ Verify Email',
                              message: `Manually verify email for ${user.full_name}?\n\nThis will mark their email as verified and unlock all free features.`,
                              confirmText: 'Verify Email',
                              confirmColor: 'green',
                              onConfirm: async () => {
                                try {
                                  const response = await fetch(`${API}/admin/users/${user.id}/verify-email`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${adminToken}` }
                                  });
                                  if (response.ok) {
                                    showNotification('✅ Email marked as verified', 'success');
                                    loadMembershipUsers();
                                  } else {
                                    showNotification('❌ Failed to verify email', 'error');
                                  }
                                } catch (error) {
                                  showNotification('❌ Error: ' + error.message, 'error');
                                }
                                setShowConfirmModal(false);
                              }
                            });
                            setShowConfirmModal(true);
                          }}
                          className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                          title="Verify email manually"
                        >
                          ✅ Verify
                        </button>
                      )}
                      
                      {!user.email_verified && (
                        <button
                          onClick={() => {
                            setConfirmModalData({
                              title: '📧 Resend Verification',
                              message: `Send verification email to:\n${user.email}?\n\nUser will receive a new verification link.`,
                              confirmText: 'Send Email',
                              confirmColor: 'blue',
                              onConfirm: async () => {
                                try {
                                  const response = await fetch(`${API}/admin/users/${user.id}/resend-verification`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${adminToken}` }
                                  });
                                  if (response.ok) {
                                    showNotification('📧 Verification email sent successfully!', 'success');
                                  } else {
                                    const error = await response.json();
                                    showNotification('❌ ' + (error.detail || 'Failed to send email'), 'error');
                                  }
                                } catch (error) {
                                  showNotification('❌ Error: ' + error.message, 'error');
                                }
                                setShowConfirmModal(false);
                              }
                            });
                            setShowConfirmModal(true);
                          }}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          title="Resend verification email"
                        >
                          📧 Resend
                        </button>
                      )}
                      
                      {user.email_verified && (
                        <button
                          onClick={() => {
                            setConfirmModalData({
                              title: '⏸️ Mark as Unverified',
                              message: `Mark ${user.full_name}'s email as unverified?\n\nThis will restrict their access to basic features only.`,
                              confirmText: 'Mark Unverified',
                              confirmColor: 'gray',
                              onConfirm: async () => {
                                try {
                                  const response = await fetch(`${API}/admin/users/${user.id}/unverify-email`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${adminToken}` }
                                  });
                                  if (response.ok) {
                                    showNotification('⏸️ Email marked as pending', 'success');
                                    loadMembershipUsers();
                                  } else {
                                    showNotification('❌ Failed to unverify email', 'error');
                                  }
                                } catch (error) {
                                  showNotification('❌ Error: ' + error.message, 'error');
                                }
                                setShowConfirmModal(false);
                              }
                            });
                            setShowConfirmModal(true);
                          }}
                          className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                          title="Mark as unverified"
                        >
                          ⏸️ Unverify
                        </button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(membershipUsers || []).filter(u =>
          (u.full_name || '').toLowerCase().includes((userSearchQuery || '').toLowerCase()) ||
          (u.email || '').toLowerCase().includes((userSearchQuery || '').toLowerCase())
        ).length === 0 && (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No users found
          </p>
        )}
      </div>
    </div>
  </div>
{showHistoryModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-5xl w-full max-h-[85vh] overflow-y-auto ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🕒 Login History <span className="text-sm font-normal text-gray-500">(Last 6 Months)</span>
        </h3>
        <button
          onClick={() => {
            setShowHistoryModal(false);
            setHistoryPage(1);
            setHistoryDateFilter('all');
            setHistoryStartDate('');
            setHistoryEndDate('');
          }}
          className="text-2xl hover:opacity-70"
        >
          ×
        </button>
      </div>
      
      {/* Date Filter Buttons + Devices tab */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => { setHistoryTab('history'); handleViewHistory(managingUser?.id || selectedUser?.id, 1, 'all'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'history' && historyDateFilter === 'all'
              ? 'bg-blue-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          All Time
        </button>
        <button
          onClick={() => { setHistoryTab('history'); handleViewHistory(managingUser?.id || selectedUser?.id, 1, 'today'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'history' && historyDateFilter === 'today'
              ? 'bg-blue-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Today
        </button>
        <button
          onClick={() => { setHistoryTab('history'); handleViewHistory(managingUser?.id || selectedUser?.id, 1, 'week'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'history' && historyDateFilter === 'week'
              ? 'bg-blue-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Last 7 Days
        </button>
        <button
          onClick={() => { setHistoryTab('history'); handleViewHistory(managingUser?.id || selectedUser?.id, 1, 'month'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'history' && historyDateFilter === 'month'
              ? 'bg-blue-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Last 30 Days
        </button>
        <button
          onClick={() => { setHistoryTab('history'); handleViewHistory(managingUser?.id || selectedUser?.id, 1, '3months'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'history' && historyDateFilter === '3months'
              ? 'bg-blue-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Last 3 Months
        </button>
        <button
          onClick={() => handleViewDevices(managingUser?.id || selectedUser?.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            historyTab === 'devices'
              ? 'bg-purple-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📱 Devices
        </button>
      </div>

      {/* Results Summary */}
      {historyTab === 'history' && historyPagination.total_count !== undefined && (
        <div className={`mb-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Showing {userHistory.length} of {historyPagination.total_count} login events
        </div>
      )}

      {/* ─── Devices tab ───────────────────────────────────────────────────── */}
      {historyTab === 'devices' && (
        <div className="mb-4">
          <div className={`mb-3 flex items-center justify-between flex-wrap gap-2`}>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {devicesLoading ? 'Loading devices...' : `${userDevices.length} active device(s) · Limit: ${devicesLimit}`}
            </div>
            {userDevices.length > 0 && (
              <button
                onClick={() => handleKickAllDevices(managingUser?.id || selectedUser?.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700"
              >
                ⚠ Sign out ALL devices
              </button>
            )}
          </div>
          <div className="space-y-2">
            {!devicesLoading && userDevices.length === 0 && (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                No active devices for this user.
              </p>
            )}
            {userDevices.map((d) => (
              <div
                key={d.device_id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
              >
                <span className="text-2xl">{d.device_type === 'phone' || d.device_type === 'tablet' ? '📱' : '💻'}</span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {d.device_label}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex flex-wrap gap-x-3`}>
                    <span>IP: {d.ip || '—'}</span>
                    <span>First seen: {d.first_seen ? new Date(d.first_seen).toLocaleString() : '—'}</span>
                    <span>Last active: {d.last_active ? new Date(d.last_active).toLocaleString() : '—'}</span>
                  </div>
                  <div className={`text-[10px] font-mono mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} truncate`}>
                    device_id: {d.device_id}
                  </div>
                </div>
                <button
                  onClick={() => handleKickDevice(managingUser?.id || selectedUser?.id, d)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                >
                  Sign out
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Login History List (history tab only) */}
      {historyTab === 'history' && (
      <>
      <div className="space-y-2 mb-4">
        {userHistory.length === 0 ? (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No login history found for the selected period
          </p>
        ) : (
          userHistory.map((event, index) => (
            <div
              key={event.id || index}
              className={`p-4 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Event Type & Time */}
                <div>
                  <span className={`font-semibold block mb-1 ${
                    event.event_type === 'login'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {event.event_type === 'login' ? '🔓 Login' : '🔒 Logout'}
                  </span>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {new Date(event.timestamp).toLocaleString()}
                  </p>
                </div>
                
                {/* IP Address */}
                <div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>IP Address</p>
                  <p className={`text-sm font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {event.ip_address}
                  </p>
                </div>
                
                {/* Device/Browser */}
                <div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Device / Browser</p>
                  {event.user_agent && (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title={event.user_agent}>
                      {event.user_agent.substring(0, 50)}...
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {historyPagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-300">
          <button
            onClick={() => handleViewHistory(managingUser?.id || selectedUser?.id, historyPage - 1, historyDateFilter, historyStartDate, historyEndDate)}
            disabled={!historyPagination.has_prev}
            className={`px-4 py-2 rounded-lg font-medium ${
              historyPagination.has_prev
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            ← Previous
          </button>
          
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Page {historyPagination.current_page} of {historyPagination.total_pages}
          </span>
          
          <button
            onClick={() => handleViewHistory(managingUser?.id || selectedUser?.id, historyPage + 1, historyDateFilter, historyStartDate, historyEndDate)}
            disabled={!historyPagination.has_next}
            className={`px-4 py-2 rounded-lg font-medium ${
              historyPagination.has_next
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next →
          </button>
        </div>
      )}
      </>
      )}

      {/* Kick Device Confirmation Modal */}
      {kickConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'}`}>
            <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {kickConfirm.all
                  ? `Sign out all ${kickConfirm.count} device${kickConfirm.count === 1 ? '' : 's'}?`
                  : 'Sign out this device?'}
              </h3>
            </div>
            <div className="p-5">
              {kickConfirm.device && (
                <div className={`flex items-center gap-3 p-3 rounded-xl mb-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <span className="text-2xl">
                    {kickConfirm.device.device_type === 'phone' || kickConfirm.device.device_type === 'tablet' ? '📱' : '💻'}
                  </span>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {kickConfirm.device.device_label}
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      IP: {kickConfirm.device.ip || '—'}
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Last active: {kickConfirm.device.last_active ? new Date(kickConfirm.device.last_active).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              )}
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {kickConfirm.all
                  ? 'The user will be signed out from EVERY device. They\'ll need to sign in again everywhere.'
                  : 'The user will be signed out from this device immediately. They\'ll see a message explaining why.'}
              </p>
            </div>
            <div className={`px-5 py-4 border-t flex items-center justify-end gap-2 ${isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gray-50'}`}>
              <button
                type="button"
                onClick={() => setKickConfirm(null)}
                disabled={kickBusy}
                className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmKick}
                disabled={kickBusy}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {kickBusy ? 'Signing out...' : (kickConfirm.all ? 'Sign out all' : 'Sign out device')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
)}

{/* Status Management Modal */}
{showStatusModal && managingUser && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-lg w-full ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Manage User Status
        </h3>
        <button
          onClick={() => setShowStatusModal(false)}
          className="text-2xl hover:opacity-70"
        >
          ×
        </button>
      </div>
      
      <div className="mb-4">
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <strong>User:</strong> {managingUser.full_name} ({managingUser.email})
        </p>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <strong>Current Status:</strong>{' '}
          <span className={`px-2 py-1 rounded text-xs ${
            managingUser.status === 'banned'
              ? 'bg-red-500 text-white'
              : managingUser.status === 'suspended'
              ? 'bg-yellow-500 text-white'
              : 'bg-green-500 text-white'
          }`}>
            {managingUser.status || 'active'}
          </span>
        </p>
        
        {managingUser.ban_reason && (
          <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              <strong>Ban Reason:</strong> {managingUser.ban_reason}
            </p>
          </div>
        )}
        
        {managingUser.suspend_reason && (
          <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
              <strong>Suspend Reason:</strong> {managingUser.suspend_reason}
            </p>
            {managingUser.suspended_until && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                <strong>Until:</strong> {(() => {
                  try {
                    const d = new Date(managingUser.suspended_until);
                    return !isNaN(d.getTime()) ? d.toLocaleString() : 'N/A';
                  } catch { return 'N/A'; }
                })()}
              </p>
            )}
          </div>
        )}
      </div>
      
      <div className="space-y-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Select Action
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setStatusAction('banned')}
              className={`py-2 px-3 rounded-lg font-medium text-sm ${
                statusAction === 'banned'
                  ? 'bg-red-600 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🚫 Ban
            </button>
            <button
              onClick={() => setStatusAction('suspended')}
              className={`py-2 px-3 rounded-lg font-medium text-sm ${
                statusAction === 'suspended'
                  ? 'bg-yellow-600 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⏸️ Suspend
            </button>
            <button
              onClick={() => setStatusAction('active')}
              className={`py-2 px-3 rounded-lg font-medium text-sm ${
                statusAction === 'active'
                  ? 'bg-green-600 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ✓ Activate
            </button>
          </div>
        </div>
        
        {statusAction === 'suspended' && (
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Suspension Duration
            </label>
            <select
              value={suspendDays}
              onChange={(e) => setSuspendDays(parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="1">1 Day</option>
              <option value="3">3 Days</option>
              <option value="7">7 Days (1 Week)</option>
              <option value="14">14 Days (2 Weeks)</option>
              <option value="30">30 Days (1 Month)</option>
              <option value="90">90 Days (3 Months)</option>
              <option value="365">365 Days (1 Year)</option>
            </select>
          </div>
        )}
        
        {(statusAction === 'banned' || statusAction === 'suspended') && (
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Reason <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(optional)</span>
            </label>
            <textarea
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder={`Optional: Why are you ${statusAction === 'banned' ? 'banning' : 'suspending'} this user?`}
              rows="3"
              className={`w-full px-3 py-2 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              This reason will be visible to other admins and shown to the user when they try to log in.
            </p>
          </div>
        )}
        
        {statusAction && (
          <div className={`p-3 rounded-lg ${
            statusAction === 'banned'
              ? isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
              : statusAction === 'suspended'
              ? isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'
              : isDarkMode ? 'bg-green-900/30' : 'bg-green-50'
          }`}>
            <p className={`text-sm font-medium ${
              statusAction === 'banned'
                ? isDarkMode ? 'text-red-300' : 'text-red-700'
                : statusAction === 'suspended'
                ? isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
                : isDarkMode ? 'text-green-300' : 'text-green-700'
            }`}>
              {statusAction === 'banned' && '🚫 This user will be permanently banned and cannot log in.'}
              {statusAction === 'suspended' && `⏸️ This user will be suspended for ${suspendDays} day${suspendDays > 1 ? 's' : ''} and cannot log in until ${new Date(Date.now() + suspendDays * 24 * 60 * 60 * 1000).toLocaleDateString()}.`}
              {statusAction === 'active' && '✓ This user will be activated and can log in normally.'}
            </p>
          </div>
        )}
        
        <div className="flex gap-2 mt-6">
          <button
            onClick={() => {
              if (!statusAction) {
                showNotification('Please select an action', 'warning');
                return;
              }
              handleUpdateUserStatus(managingUser.id, statusAction);
            }}
            disabled={!statusAction}
            className={`flex-1 py-2 rounded-lg font-medium ${
              statusAction
                ? statusAction === 'banned'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : statusAction === 'suspended'
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
                : isDarkMode
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Apply Changes
          </button>
          <button
            onClick={() => {
              setShowStatusModal(false);
              setManagingUser(null);
              setStatusReason('');
              setSuspendDays(7);
              setStatusAction('');
            }}
            className={`px-6 py-2 rounded-lg font-medium ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
            }`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Deletion Confirmation Modal */}
{deletionConfirmModal.show && deletionConfirmModal.request && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-xl shadow-2xl p-6 max-w-md w-full ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      {deletionConfirmModal.action === 'delete' ? (
        <>
          {/* Delete Account Confirmation */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-4xl">⚠️</span>
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Permanently Delete Account?
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              You are about to delete <strong className="text-red-500">{deletionConfirmModal.request.user_name}</strong>'s account
            </p>
          </div>
          
          <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              ⚠️ This action CANNOT be undone!
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              All of this user's data will be permanently deleted:
            </p>
            <ul className={`text-sm mt-2 ml-4 space-y-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              <li>• Profile information & settings</li>
              <li>• Favorites & saved translations</li>
              <li>• Learning progress & badges</li>
              <li>• Orders & payment history</li>
              <li>• All messages & conversations</li>
            </ul>
          </div>
          
          <div className={`p-3 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <strong>User:</strong> {deletionConfirmModal.request.user_email}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <strong>Reason:</strong> {deletionConfirmModal.request.reason || 'Not specified'}
            </p>
            {deletionConfirmModal.request.details && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <strong>Details:</strong> {deletionConfirmModal.request.details}
              </p>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setDeletionConfirmModal({ show: false, request: null, action: null })}
              className={`flex-1 py-3 px-4 rounded-lg font-medium ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/deletion-requests/${deletionConfirmModal.request.id}/complete`, {
                    method: 'DELETE',
                    headers: authHeaders
                  });
                  if (res.ok) {
                    showNotification('User account deleted permanently', 'success');
                    setDeletionConfirmModal({ show: false, request: null, action: null });
                    loadTabData();
                  } else {
                    showNotification('Failed to delete account', 'error');
                  }
                } catch (err) {
                  showNotification('Failed to delete account', 'error');
                }
              }}
              className="flex-1 py-3 px-4 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white"
            >
              🗑️ Yes, Delete Permanently
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Keep Account Confirmation */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <span className="text-4xl">✓</span>
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Keep This Account?
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Cancel the deletion request for <strong className="text-green-500">{deletionConfirmModal.request.user_name}</strong>
            </p>
          </div>
          
          <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
              ✓ The user's account will remain active<br/>
              ✓ All their data will be preserved<br/>
              ✓ They can continue using the platform
            </p>
          </div>
          
          <div className={`p-3 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <strong>User:</strong> {deletionConfirmModal.request.user_email}
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <strong>Reason for leaving:</strong> {deletionConfirmModal.request.reason || 'Not specified'}
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setDeletionConfirmModal({ show: false, request: null, action: null })}
              className={`flex-1 py-3 px-4 rounded-lg font-medium ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/deletion-requests/${deletionConfirmModal.request.id}/cancel`, {
                    method: 'PUT',
                    headers: authHeaders
                  });
                  if (res.ok) {
                    showNotification('Deletion request cancelled - account kept', 'success');
                    setDeletionConfirmModal({ show: false, request: null, action: null });
                    loadTabData();
                  } else {
                    showNotification('Failed to cancel request', 'error');
                  }
                } catch (err) {
                  showNotification('Failed to cancel request', 'error');
                }
              }}
              className="flex-1 py-3 px-4 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white"
            >
              ✓ Yes, Keep Account
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

{/* User Management Modal */}
{showUserManagementModal && managingUser && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          User Management
        </h3>
        <button
          onClick={handleCloseUserManagement}
          className="text-3xl hover:opacity-70"
        >
          ×
        </button>
      </div>
      
      <div className={`p-4 rounded-lg mb-6 ${
        isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Full Name</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {managingUser.full_name}
            </p>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Email</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {managingUser.email}
            </p>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
            <span className={`text-xs px-2 py-1 rounded-full inline-block ${
              managingUser.status === 'banned' 
                ? 'bg-red-500 text-white'
                : managingUser.status === 'suspended'
                ? 'bg-yellow-500 text-white'
                : 'bg-green-500 text-white'
            }`}>
              {managingUser.status || 'active'}
            </span>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Plan</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {managingUser.plan?.display_name || 'No plan'}
            </p>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Member Since</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {(() => {
                try {
                  const d = new Date(managingUser.created_at);
                  return !isNaN(d.getTime()) ? d.toLocaleDateString() : 'N/A';
                } catch { return 'N/A'; }
              })()}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {(() => {
                try {
                  const d = new Date(managingUser.created_at);
                  return !isNaN(d.getTime()) ? d.toLocaleTimeString() : '';
                } catch { return ''; }
              })()}
            </p>
          </div>
          <div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Last Login</p>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {(() => {
                try {
                  if (managingUser.last_login) {
                    const d = new Date(managingUser.last_login);
                    return !isNaN(d.getTime()) ? d.toLocaleDateString() : 'Never';
                  }
                  return 'Never';
                } catch { return 'Never'; }
              })()}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              IP: {managingUser.last_ip || 'N/A'}
            </p>
          </div>
        </div>
      </div>
      
      <div className={`p-4 rounded-lg mb-4 ${
        isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Edit User Information
        </h4>
        <div className="space-y-3">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Full Name
            </label>
            <input
              type="text"
              value={editingUser?.full_name || ''}
              onChange={(e) => setEditingUser(prev => ({
                ...prev,
                full_name: e.target.value
              }))}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-600 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Email
            </label>
            <input
              type="email"
              value={editingUser?.email || ''}
              onChange={(e) => setEditingUser(prev => ({
                ...prev,
                email: e.target.value
              }))}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-600 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              New Password <span className="text-xs text-gray-500">(leave empty to keep current)</span>
            </label>
            <input
              type="password"
              value={editingUser?.password || ''}
              onChange={(e) => setEditingUser(prev => ({
                ...prev,
                password: e.target.value
              }))}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-600 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter new password"
            />
          </div>
          
          {/* Subscription Management Section */}
          <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'border-indigo-600 bg-indigo-900/20' : 'border-indigo-300 bg-indigo-50'}`}>
            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📦 Subscription Management
            </h4>
            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Membership Plan
                </label>
                <select
                  value={editingSubscription?.plan_id || managingUser.subscription?.plan_id || ''}
                  onChange={(e) => setEditingSubscription({
                    ...editingSubscription,
                    plan_id: e.target.value,
                    plan: membershipPlans.find(p => p.id === e.target.value)
                  })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {membershipPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.display_name} - ${plan.price}/{plan.billing_period}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={editingSubscription?.start_date || (() => {
                      try {
                        if (managingUser.subscription?.start_date) {
                          const d = new Date(managingUser.subscription.start_date);
                          return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
                        }
                        return '';
                      } catch { return ''; }
                    })()}
                    onChange={(e) => setEditingSubscription({
                      ...editingSubscription,
                      start_date: e.target.value
                    })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={editingSubscription?.end_date || (() => {
                      try {
                        if (managingUser.subscription?.end_date) {
                          const d = new Date(managingUser.subscription.end_date);
                          return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
                        }
                        return '';
                      } catch { return ''; }
                    })()}
                    onChange={(e) => setEditingSubscription({
                      ...editingSubscription,
                      end_date: e.target.value
                    })}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </label>
                <select
                  value={editingSubscription?.status || managingUser.subscription?.status || 'active'}
                  onChange={(e) => setEditingSubscription({
                    ...editingSubscription,
                    status: e.target.value
                  })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <button
                onClick={async () => {
                  try {
                    const formData = new FormData();
                    formData.append('plan_id', editingSubscription?.plan_id || managingUser.subscription?.plan_id);
                    formData.append('status', editingSubscription?.status || 'active');
                    if (editingSubscription?.start_date) {
                      formData.append('start_date', editingSubscription.start_date);
                    }
                    if (editingSubscription?.end_date) {
                      formData.append('end_date', editingSubscription.end_date);
                    }
                    
                    const response = await fetch(`${API}/admin/membership/users/${managingUser.id}/subscription`, {
                      method: 'PUT',
                      headers: authHeadersForForm,
                      body: formData
                    });
                    
                    if (response.ok) {
                      showNotification('Subscription updated successfully', 'success');
                      setEditingSubscription(null);
                      loadTabData();
                    } else {
                      showNotification('Failed to update subscription', 'error');
                    }
                  } catch (error) {
                    console.error('Error updating subscription:', error);
                    showNotification('Failed to update subscription', 'error');
                  }
                }}
                className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                💳 Update Subscription
              </button>
              {/* Cancel Membership Immediately — only show if user has a paid plan */}
              {managingUser.plan?.name !== 'free' && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Cancel membership for ${managingUser.full_name} IMMEDIATELY? This will set their plan to Free right now.`)) return;
                    try {
                      const freePlan = membershipPlans.find(p => p.name === 'free');
                      if (!freePlan) {
                        showNotification('Free plan not found in system', 'error');
                        return;
                      }
                      const formData = new FormData();
                      formData.append('plan_id', freePlan.id);
                      formData.append('status', 'cancelled');
                      formData.append('end_date', new Date().toISOString());
                      const response = await fetch(`${API}/admin/membership/users/${managingUser.id}/subscription`, {
                        method: 'PUT',
                        headers: authHeadersForForm,
                        body: formData
                      });
                      if (response.ok) {
                        showNotification('Membership cancelled immediately', 'success');
                        setEditingSubscription(null);
                        loadTabData();
                      } else {
                        const err = await response.json().catch(() => ({}));
                        showNotification(err.detail || 'Failed to cancel membership', 'error');
                      }
                    } catch (error) {
                      showNotification('Error cancelling membership: ' + error.message, 'error');
                    }
                  }}
                  className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium mt-2"
                >
                  ❌ Cancel Membership Immediately
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={() => handleSaveUser()}
            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            💾 Save Changes
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <button
          onClick={() => {
            handleCloseUserManagement(true); // Keep user
            handleViewHistory(managingUser.id);
          }}
          className="py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
        >
          📜 Login History
        </button>
        <button
          onClick={() => {
            handleCloseUserManagement(true); // Keep user
            handleOpenStatusModal(managingUser);
          }}
          className="py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
        >
          ⚡ Manage Status
        </button>
      </div>
      
      <button
        onClick={handleCloseUserManagement}
        className={`w-full py-2 rounded-lg font-medium ${
          isDarkMode
            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
            : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
        }`}
      >
        Close
      </button>
    </div>
  </div>
)}

{/* Confirmation Modal */}
{showConfirmModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
      <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {confirmModalData.title}
      </h3>
      <p className={`text-sm mb-6 whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {confirmModalData.message}
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => setShowConfirmModal(false)}
          className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
            isDarkMode 
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={confirmModalData.onConfirm}
          className={`flex-1 px-4 py-2 text-white rounded-lg font-semibold ${
            confirmModalData.confirmColor === 'red' 
              ? 'bg-red-600 hover:bg-red-700' 
              : confirmModalData.confirmColor === 'green'
              ? 'bg-green-600 hover:bg-green-700'
              : confirmModalData.confirmColor === 'blue'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
        >
          {confirmModalData.confirmText}
        </button>
      </div>
    </div>
  </div>
)}

{/* Email User Modal */}
{showEmailUserModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-lg w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          ✉️ Email User
        </h3>
        <button
          onClick={() => {
            setShowEmailUserModal(false);
            setEmailUserData({ userId: '', userEmail: '', userName: '', subject: '', message: '' });
          }}
          className="text-gray-500 hover:text-gray-700 text-2xl"
        >
          ×
        </button>
      </div>

      <div className="space-y-4">
        {/* User Info */}
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <strong>To:</strong> {emailUserData.userName}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {emailUserData.userEmail}
          </p>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <strong>From:</strong> DarijaPro &lt;noreply@darijapro.com&gt;
          </p>
        </div>

        {/* Subject */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={emailUserData.subject}
            onChange={(e) => setEmailUserData({...emailUserData, subject: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="Subject line..."
            required
          />
        </div>

        {/* Message */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            value={emailUserData.message}
            onChange={(e) => setEmailUserData({...emailUserData, message: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="Your message to the user..."
            rows="6"
            required
          />
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Email will be sent with professional DarijaPro template
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => {
            setShowEmailUserModal(false);
            setEmailUserData({ userId: '', userEmail: '', userName: '', subject: '', message: '' });
          }}
          className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
            isDarkMode 
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            if (!emailUserData.subject || !emailUserData.message) {
              showNotification('⚠️ Please fill in subject and message', 'error');
              return;
            }

            setConfirmModalData({
              title: '📧 Send Email',
              message: `Send email to ${emailUserData.userName}?\n\nTo: ${emailUserData.userEmail}\nSubject: ${emailUserData.subject}`,
              confirmText: 'Send Email',
              confirmColor: 'blue',
              onConfirm: async () => {
                try {
                  const response = await fetch(`${API}/admin/users/${emailUserData.userId}/send-email`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${adminToken}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      subject: emailUserData.subject,
                      message: emailUserData.message
                    })
                  });

                  if (response.ok) {
                    showNotification(`✅ Email sent to ${emailUserData.userEmail}!`, 'success');
                    setShowEmailUserModal(false);
                    setEmailUserData({ userId: '', userEmail: '', userName: '', subject: '', message: '' });
                  } else {
                    const error = await response.json();
                    showNotification('❌ Failed: ' + (error.detail || 'Unknown error'), 'error');
                  }
                } catch (error) {
                  showNotification('❌ Error: ' + error.message, 'error');
                }
                setShowConfirmModal(false);
              }
            });
            setShowConfirmModal(true);
          }}
          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
        >
          📧 Send Email
        </button>
      </div>
    </div>
  </div>
)}

{/* Add User Modal */}
{showAddUserModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          ➕ Add New User
        </h3>
        <button
          onClick={() => {
            setShowAddUserModal(false);
            setNewUserData({ email: '', password: '', full_name: '', username: '', planId: '' });
          }}
          className="text-gray-500 hover:text-gray-700 text-2xl"
        >
          ×
        </button>
      </div>

      <div className="space-y-4">
        {/* Full Name */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newUserData.full_name}
            onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="John Doe"
            required
          />
        </div>

        {/* Email */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={newUserData.email}
            onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="user@example.com"
            required
          />
        </div>

        {/* Username */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Username (Optional)
          </label>
          <input
            type="text"
            value={newUserData.username}
            onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="johndoe"
          />
        </div>

        {/* Password */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={newUserData.password}
            onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="••••••••"
            required
          />
        </div>

        {/* Membership Plan */}
        <div>
          <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Membership Plan (Optional)
          </label>
          <select
            value={newUserData.planId}
            onChange={(e) => setNewUserData({...newUserData, planId: e.target.value})}
            className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          >
            <option value="">No Plan (Free)</option>
            {membershipPlans.filter(p => p.name !== 'free').map(plan => (
              <option key={plan.id} value={plan.id}>
                {plan.display_name} - ${plan.price}/{plan.billing_period}
              </option>
            ))}
          </select>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            If selected, user will get 12 months of this plan
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => {
            setShowAddUserModal(false);
            setNewUserData({ email: '', password: '', full_name: '', username: '', planId: '' });
          }}
          className={`flex-1 px-4 py-2 border rounded-lg font-semibold ${
            isDarkMode 
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={createNewUser}
          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
        >
          ➕ Create User
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
};

export default MembershipAdmin;
